import { animated, easings, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useTouchScreen } from '#src/hooks/device/useTouchScreen.ts';

const IDLE_DURATION_MS = 6000;
const IDLE_RESTART_DELAY_MS = 1000;

const Root = styled.div`
  position: relative;
  height: 100%;
`;

const SpringLayer = styled(animated.div)`
  height: 100%;
`;

type Props = {
  idle?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

export const InteractiveElement = ({
  idle: idleProp = false,
  disabled = false,
  className,
  children,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const isTouchScreen = useTouchScreen();
  const idle = isTouchScreen || idleProp;
  const disableEffect = disabled || (isTouchScreen && !idleProp);
  const restartTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [styles, api] = useSpring(() => ({
    from: { '--mx': 0.5, '--my': 0.5, '--active': 0 },
    config: { mass: 5, tension: 350, friction: 40 },
  }));

  const startIdleLoop = useCallback(() => {
    api.start({
      to: [
        { '--mx': 0, '--my': 0, '--active': 1 },
        { '--mx': 1, '--my': 1, '--active': 1 },
      ],
      loop: {
        from: { '--mx': 0, '--my': 0, '--active': 1 },
      },
      config: {
        duration: IDLE_DURATION_MS / 2,
        easing: easings.linear,
      },
      delay: 1000,
    });
  }, [api]);

  useEffect(() => {
    if (!idle) return () => {};
    startIdleLoop();
    return () => {
      api.stop();
      clearTimeout(restartTimerRef.current);
    };
  }, [isTouchScreen, idle, api, startIdleLoop]);

  const dragBind = useDrag(
    ({ xy: [px, py], first, last }) => {
      if (disabled) return;
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;

      if (first) {
        clearTimeout(restartTimerRef.current);
        api.stop();
      }

      const mx = Math.min(1, Math.max(0, (px - rect.left) / rect.width));
      const my = Math.min(1, Math.max(0, (py - rect.top) / rect.height));
      api.start({
        '--mx': mx,
        '--my': my,
        '--active': 1,
        config: {
          mass: 1,
          tension: 600,
          friction: 30,
          duration: undefined,
          easing: undefined,
        },
      });

      if (last) {
        api.start({
          '--active': 0,
          config: {
            mass: 5,
            tension: 350,
            friction: 40,
            duration: undefined,
            easing: undefined,
          },
        });
        restartTimerRef.current = setTimeout(
          startIdleLoop,
          IDLE_RESTART_DELAY_MS
        );
      }
    },
    { pointer: { touch: true } }
  );

  const springStyle = {
    '--mx': styles['--mx'].to(v => v),
    '--my': styles['--my'].to(v => v),
    '--active': styles['--active'].to(v => v),
  } as CSSProperties;

  return (
    <Root className={classNames(className)}>
      {disableEffect ? (
        children
      ) : isTouchScreen ? (
        <SpringLayer ref={ref} {...dragBind()} style={springStyle}>
          {children}
        </SpringLayer>
      ) : (
        <SpringLayer
          ref={ref}
          onMouseMove={({ clientX: mx, clientY: my }) => {
            api.stop();
            const rect = ref?.current?.getBoundingClientRect();
            if (rect) {
              api.start({
                '--mx': (mx - rect.left) / rect.width,
                '--my': (my - rect.top) / rect.height,
                '--active': 1,
                config: { mass: 1, tension: 6000, friction: 100 },
              });
            }
          }}
          onMouseLeave={() => {
            api.start({ '--active': 0 });
            if (idle) {
              startIdleLoop();
            }
          }}
          style={springStyle}
        >
          {children}
        </SpringLayer>
      )}
    </Root>
  );
};
